import { useState } from 'react'
import { Box, List, ListItemButton, ListItemText, Typography, Chip, Tooltip } from '@mui/material'
import { ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { OrchestrationDto } from '../../types/Constants'
import OrchestrationTimeline from '../../components/OrchestrationTimeline'
import PageSpinner from '@src/shared/components/PageSpinner'

interface OrchestrationPanelProps {
  orchestrations: OrchestrationDto[]
  isLoading: boolean
}

const statusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
  switch (status) {
    case 'Completed':
      return 'success'
    case 'Failed':
      return 'error'
    case 'Running':
      return 'warning'
    default:
      return 'default'
  }
}

const OrchestrationPanel = ({ orchestrations, isLoading }: OrchestrationPanelProps) => {
  const { t } = useTranslation()
  const [selectedId, setSelectedId] = useState<string | null>(null)

  if (isLoading) return <PageSpinner isLoading />

  if (orchestrations.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        {t('investigation.orchestrations.noOrchestrations')}
      </Typography>
    )
  }

  const selected = orchestrations.find((o) => o.instanceId === selectedId) ?? null

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 250px)', gap: 2 }}>
      <Box sx={{ width: 350, borderRight: 1, borderColor: 'divider', overflow: 'auto' }}>
        <List dense>
          {orchestrations.map((orch) => (
            <ListItemButton
              key={orch.instanceId}
              selected={selectedId === orch.instanceId}
              onClick={() => setSelectedId(orch.instanceId)}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip label={orch.runtimeStatus} size="small" color={statusColor(orch.runtimeStatus)} />
                    {orch.hasErrors && (
                      <Tooltip title={t('investigation.orchestrations.hasFailedTasks', 'Contains failed task(s)')}>
                        <ErrorOutlineIcon fontSize="small" color="error" />
                      </Tooltip>
                    )}
                    <Typography variant="body2" noWrap>
                      {orch.name}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {orch.instanceId}
                  </Typography>
                }
              />
            </ListItemButton>
          ))}
        </List>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {selected ? (
          <OrchestrationTimeline instanceId={selected.instanceId} activities={selected.activities} />
        ) : (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            {t('investigation.orchestrations.noOrchestrations')}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default OrchestrationPanel
